<script setup lang="ts">
import { ref, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import Button from 'primevue/button';
import LanguageButtonGroup from "./LanguageButtonGroup.vue";
import AddLanguageDialog from "./AddLanguageDialog.vue";
import type { MaintenanceLanguage } from "../../types/TextMaintenanceTypes";

defineProps({
    selectedProduct: {
        type: String,
        required: true,
    },
    availableLanguages: {
        type: Array as () => MaintenanceLanguage[],
        required: true,
    },
    hasLanguageChanges: {
        type: Function as PropType<(languageCode: string) => boolean>,
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    loadingLanguages: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    "language-select",
    "add-language"
]);

const { t } = useI18n();

// Add Language Dialog
const showAddLanguageDialog = ref(false);

const handleLanguageSelect = (selectedLanguageCodes: string[]) => {
    emit("language-select", selectedLanguageCodes);
};

const handleAddLanguage = () => {
    showAddLanguageDialog.value = true;
};

const handleAddLanguageConfirm = (languageData: { code: string; name: string }) => {
    emit("add-language", languageData);
    showAddLanguageDialog.value = false;
};
</script>

<template>
    <div>
        <!-- Loading Languages -->
        <div v-if="selectedProduct && loadingLanguages" class="w-full mt-8">
            <div class="flex justify-between items-center mb-2">
                <div class="block text-sm font-medium section-title">
                    {{ t("textMaintenance.languages") }}
                </div>
            </div>
            <div class="loading-languages text-center py-6">
                <i class="pi pi-spin pi-spinner text-2xl text-brand mb-2"></i>
                <p class="text-content-muted">{{ t('textMaintenance.loadingLanguages') }}</p>
            </div>
        </div>
        
        <!-- Language Selection -->
        <div v-else-if="selectedProduct && availableLanguages.length > 0" class="w-full mt-8">
            <div class="flex justify-between items-center mb-2">
                <label for="language-button-group" class="block text-sm font-medium section-title">
                    {{ t("textMaintenance.languages") }}
                </label>
                <Button
                    :label="t('textMaintenance.addLanguage')"
                    icon="pi pi-plus"
                    severity="secondary"
                    size="small"
                    @click="handleAddLanguage"
                    :disabled="loading"
                    data-cy="text-maintenance_add-language-button"
                />
            </div>
            <LanguageButtonGroup
                id="language-button-group"
                :languages="availableLanguages"
                :has-language-changes="hasLanguageChanges"
                :max-selection="2"
                @language-select="handleLanguageSelect"
                data-cy="text-maintenance_language-button-group"
            />
        </div>
        
        <!-- No available languages -->
        <div v-else-if="selectedProduct && !loading && !loadingLanguages" class="w-full">
            <div class="text-center py-6">
                <i class="pi pi-info-circle text-2xl text-content-muted mb-2"></i>
                <p class="text-content-muted">{{ t('textMaintenance.noLanguagesAvailable') }}</p>
                <Button
                    :label="t('textMaintenance.addLanguage')"
                    icon="pi pi-plus"
                    severity="primary"
                    size="small"
                    @click="handleAddLanguage"
                    class="mt-2"
                    data-cy="text-maintenance_add-language-empty-button"
                />
            </div>
        </div>

        <AddLanguageDialog
            v-model:visible="showAddLanguageDialog"
            @add-language="handleAddLanguageConfirm"
        />
    </div>
</template>

<style scoped>
.section-title {
    font-weight: 900 !important;
    color: var(--p-secondary-500) !important;
    font-size: 0.925rem;
}
</style>